import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, Input, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GTM_EVENT_CONSTANT, PARTNER_REGISTRATION_MODE, PartnerRegistrationMode } from '@constants/app.constants';
import { environment } from '@environments/environment';
import { PartnerFormModel } from '@models/partner-form.model';
import { TranslateModule } from '@ngx-translate/core';
import { CalioSafePipe } from "@pipe/calio-safe.pipe";
import { AnalyticsService } from '@services/analytics.service';
import { HttpClientService } from '@services/http-client.service';

@Component({
  selector: 'app-final-step',
  templateUrl: './final-step.component.html',
  styleUrls: ['./final-step.component.scss'],
  imports: [TranslateModule, CalioSafePipe]
})
export class FinalStepComponent implements OnInit {
  @Input() protected partner: PartnerFormModel;
  @Input() registrationMode: PartnerRegistrationMode = PARTNER_REGISTRATION_MODE.PLG;
  // True when the email was already proven via the pre-signup email OTP
  // (require_email_confirmation). In that case the legacy "verify your email via
  // the link we sent" prompt + resend button are redundant, so we suppress them
  // and show the verified-account copy instead.
  @Input() emailOtpVerified = false;

  protected get isSLGMode(): boolean {
    return this.registrationMode === PARTNER_REGISTRATION_MODE.SLG;
  }

  private analyticsService = inject(AnalyticsService);
  private httpClientService = inject(HttpClientService);
  private destroyRef = inject(DestroyRef);

  // H4: reflect the CREATED partner's actual approval state, not the brand's support_must_verify
  // proxy. A signup can be pending (is_verified_by_support = 0) even when support_must_verify is off
  // -- a "test"-flagged name, SLG, or an SLG-default brand -- so keying off the policy wrongly told a
  // pending account it was "ready". Default to pending (the review copy) when the flag is absent.
  protected get isPendingSupportApproval(): boolean {
    return !this.partner?.is_verified_by_support;
  }

  protected readonly gtmEventConstant = GTM_EVENT_CONSTANT;
  protected readonly redirectDomain = environment.redirectDomain;
  // Set only for JRNI brands; empty for Calenso. Rendered as a visible
  // button (below) instead of an auto-opened tab that popup blockers kill.
  protected readonly thankYouUrl = environment.brandUrls.BRAND_THANK_YOU_URL;
  protected readonly supportEmail = environment.brandUrls.BRAND_SUPPORT_EMAIL;

  protected isResendEmailSend$ = signal(false);
  protected remainingTime$ = signal(60);
  protected blockForTimeMinutes$ = signal(0);
  protected blockForTimeSeconds$ = signal(0);
  protected statusMessage$ = signal('');

  ngOnInit(): void {
    this.analyticsService.emitGTMEvent(this.gtmEventConstant.STEP_3);
  }

  protected resendMail(): void {
    this.httpClientService.authServicePost(`partners/send_verification_email/${this.partner.uuid}`, {})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          if (result?.status) {
            this.isResendEmailSend$.set(true);
            this.startCountDown();
          }
        },
        error: (error: HttpErrorResponse) => {
          const errorState = error?.error?.error;

          // User will be block, if he makes too many requests
          if (errorState === 'TooManyRequestsException') {
            if (error.headers.get('Retry-After')) {
              const retryAfter = +error.headers.get('Retry-After');
              if (retryAfter > 60) {
                this.blockForTimeMinutes$.set(Math.floor(retryAfter / 60));
              } else {
                this.blockForTimeSeconds$.set(+error.headers.get('Retry-After'));
              }
            } else {
              this.statusMessage$.set('TooManyRequestsException'.toLowerCase());
            }
            return;
          }

          this.statusMessage$.set('SomeThingWentWrong'.toLowerCase());
          if ([
            'BadRequestException',
            'IpNotWhitelistedException',
            'ResendLimitReachedException'
          ].includes(errorState)) {
            this.statusMessage$.set(errorState.toLowerCase());
          }
        }
      });
  }

  protected startCountDown(): void {
    setTimeout(() => {
      if (this.remainingTime$() < 2) {
        this.remainingTime$.set(60);
        this.isResendEmailSend$.set(false);
      } else {
        this.remainingTime$.update(time => time - 1);
        this.startCountDown();
      }
    }, 1000)
  }
}
